import { Field, FormikHelpers, FormikProps } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import FormProvider from "../../../components/forms/FormProvider";
import Input from "../../../components/forms/Input";
import MessageComponent from "../../../components/message";
import { useContacts } from "../../../context/contacts";
import { useMessages } from "../../../context/messages";

const ContactMessagesTab: React.FC<
  RouteComponentProps<{ id: string }>
> = () => {
  const { currentContact } = useContacts();
  const { hostMessages, addContactMessage, fetchContactMessages } =
    useMessages();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!currentContact?.uuid) return;

    fetchContactMessages(`${currentContact.uuid}`);
  }, [currentContact?.uuid, fetchContactMessages]);

  const messages = useMemo(
    () => hostMessages[`${currentContact?.uuid}`] || [],
    [currentContact?.uuid, hostMessages]
  );

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      try {
        await addContactMessage(formValues.message, `${currentContact?.uuid}`);
        formikHelpers.resetForm();

        formikHelpers.setSubmitting(false);
      } catch (error) {
        setErrorMessage("Something went wrong");
        formikHelpers.setSubmitting(false);
      }
    },
    [addContactMessage, currentContact?.uuid]
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mt-6 pb-5">
        <p className="mt-2 max-w-4xl text-sm text-gray-500 dark:text-gray-300">
          Here you can send messages back and forth to your hotspot
          administrator(s). They will receive an email with your message every
          time you send a message and vice versa.
        </p>
      </div>
      <div className="max-w-5xl mx-auto">
        {messages.map((message, index) => (
          <div className="py-4" key={index}>
            <MessageComponent message={message} />
          </div>
        ))}
      </div>

      <div className="w-full mt-2">
        <FormProvider
          error={errorMessage}
          submitButtonText={"Send a message"}
          submittingSubmitButtonText={"Sending"}
          classnames={{
            wrapper: "w-full sm:max-w-none",
            buttonWrapper: "pr-0 pb-0"
          }}
          formikProps={{
            onSubmit,
            initialValues: { message: "" },
            validationSchema: Yup.object().shape({
              message: Yup.string().required("Required")
            })
          }}
          orderedFieldNames={ORDERED_FIELD_NAMES}
        >
          {({ isSubmitting }: FormikProps<FormValues>) => {
            return (
              <Field
                id="message"
                name="message"
                placeholder="Type a message"
                component={Input}
                textarea
                withoutErrorLabel
                rows={3}
                styles={{
                  inputContainer: { maxWidth: "unset" }
                }}
                classnames={{
                  input: "w-full"
                }}
                disabled={isSubmitting}
              />
            );
          }}
        </FormProvider>
      </div>
    </div>
  );
};

export default ContactMessagesTab;

//
// Utils
//

interface FormValues {
  message: string;
}

const ORDERED_FIELD_NAMES = ["message"];
